import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { motion } from 'framer-motion';
import { FiClock, FiUser, FiActivity } from 'react-icons/fi';
import { format } from 'date-fns';

const ActivityLog = ({ taskId }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (taskId) {
      fetchActivities();
    } else {
      fetchAllActivities();
    }
  }, [taskId]);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const endpoint = taskId ? `/activity/task/${taskId}` : '/activity';
      const response = await api.get(endpoint);
      setActivities(response.data.activities || []);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllActivities = async () => {
    setLoading(true);
    try {
      const response = await api.get('/activity');
      setActivities(response.data.activities || []);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (actionType) => {
    if (actionType.includes('created')) return '✨';
    if (actionType.includes('updated') || actionType.includes('status')) return '🔄';
    if (actionType.includes('completed')) return '✅';
    if (actionType.includes('deleted')) return '🗑️';
    if (actionType.includes('comment')) return '💬';
    if (actionType.includes('tag')) return '🏷️';
    return '📝';
  };

  const getActivityColor = (actionType) => {
    if (actionType.includes('created')) return 'text-green-400';
    if (actionType.includes('updated') || actionType.includes('status')) return 'text-blue-400';
    if (actionType.includes('completed')) return 'text-green-400';
    if (actionType.includes('deleted')) return 'text-red-400';
    return 'text-white/60';
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <div className="flex items-center space-x-2 mb-4">
        <FiActivity className="text-white/80" size={20} />
        <h3 className="text-white font-semibold text-lg">Activity Log ({activities.length})</h3>
      </div>

      <div className="space-y-3 max-h-96 overflow-y-auto">
        {activities.length === 0 ? (
          <p className="text-white/60 text-center py-4">No activity yet</p>
        ) : (
          activities.map((activity) => (
            <motion.div
              key={activity.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white/5 rounded-lg p-3 border border-white/10"
            >
              <div className="flex items-start space-x-3">
                <span className="text-2xl">{getActivityIcon(activity.action_type)}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    {activity.user_name && (
                      <span className="text-white font-medium text-sm">{activity.user_name}</span>
                    )}
                    <span className={`text-xs ${getActivityColor(activity.action_type)}`}>
                      {activity.action_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  </div>
                  <p className="text-white/80 text-sm">{activity.description}</p>
                  {activity.task_title && (
                    <p className="text-white/60 text-xs mt-1">Task: {activity.task_title}</p>
                  )}
                  {activity.team_name && (
                    <p className="text-white/60 text-xs">Team: {activity.team_name}</p>
                  )}
                  <div className="flex items-center space-x-1 mt-2">
                    <FiClock size={12} className="text-white/40" />
                    <span className="text-white/40 text-xs">
                      {format(new Date(activity.created_at), 'MMM dd, yyyy HH:mm')}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default ActivityLog;
